<template>
  <div class="notifications-page">
    <AppSectionHero
      :title="pageText.heroTitle"
      :subtitle="pageText.heroSubtitle"
      :stats="heroStats"
      :show-refresh="false"
      @stat-click="selectStatusCard"
    />

    <CCard class="border-0 shadow-sm filter-shell">
      <CCardBody class="p-3 p-lg-4">
        <div class="toolbar">
          <div class="search-box">
            <span class="search-icon">🔍</span>
            <input
              v-model="searchQuery"
              type="text"
              :placeholder="pageText.searchPlaceholder"
            >
          </div>
          <div class="toolbar-filters">
            <input
              v-model="startDate"
              type="date"
              class="date-input"
              :aria-label="pageText.startDateLabel"
            >
            <input
              v-model="endDate"
              type="date"
              class="date-input"
              :aria-label="pageText.endDateLabel"
            >
          </div>
        </div>
      </CCardBody>
    </CCard>

    <CCard class="border-0 shadow-sm notifications-shell">
      <CCardBody class="p-0">
        <div class="notifications-head">
          <div class="notifications-head__copy">{{ notificationsCountLabel }}</div>
        </div>
        <section class="notifications-list">
          <article
            v-for="item in filteredNotifications"
            :key="item.id"
            class="notification-card"
            :class="{ 'notification-card--unread': isUnread(item) }"
            role="button"
            tabindex="0"
            @click="openNotification(item)"
            @keydown.enter.prevent="openNotification(item)"
            @keydown.space.prevent="openNotification(item)"
          >
            <div class="notification-top">
              <div>
                <div class="notification-source">{{ item.source }}</div>
                <h2>{{ item.title }}</h2>
              </div>
              <div class="notification-badges">
                <span class="notification-deadline" :class="'notification-deadline--' + deadlineState(item)">
                  {{ deadlineLabel(item) }}
                </span>
                <button
                  type="button"
                  class="notification-dismiss"
                  :aria-label="pageText.dismissNotification"
                  :title="pageText.dismissNotification"
                  @click.stop="dismissNotification(item)"
                >
                  <CIcon name="cil-x" />
                </button>
              </div>
            </div>

            <p class="notification-message">{{ item.message }}</p>

            <div class="notification-meta">
              <span v-if="notificationContextLabel(item)">{{ notificationContextLabel(item) }}</span>
              <span>{{ formatDateTime(item.timestamp) }}</span>
            </div>

            <div class="notification-action">
              <span class="notification-next">{{ item.nextStep }}</span>
            </div>
          </article>

          <div v-if="!filteredNotifications.length" class="empty-state">
            {{ pageText.emptyState }}
          </div>
        </section>
      </CCardBody>
    </CCard>
  </div>
</template>

<script>
import AppSectionHero from '@/projects/components/layout/AppSectionHero'
import { canAutoRefresh } from '@/projects/utils/refresh-guard'
import {
  detectRouteRole,
  getActiveWorkspaceRole,
  getAssignedRoles,
  getWorkspaceRole,
  workspaceRoleEventName
} from '@/utils/role-dashboard'
import {
  buildWorkflowNotificationsForRoles,
  dedupeWorkflowNotifications,
  dismissWorkflowNotification,
  listWorkflowNotificationsForRoles,
  markWorkflowNotificationRead,
  mergeStoredWorkflowNotificationState,
  workflowNotificationMatches,
  workflowNotificationEventName
} from '@/utils/workflow-notifications'
import { formatDateTimeWithPreferences } from '@/utils/workspace-preferences'

const PAGE_TEXT = {
  en: {
    heroTitle: 'Notifications',
    heroSubtitle: 'Open the item, continue the work, and move forward.',
    searchPlaceholder: 'Search by agreement name',
    startDateLabel: 'Start date',
    endDateLabel: 'End date',
    emptyState: 'No notifications found.',
    dismissNotification: 'Dismiss notification',
    referencePrefix: 'Ref',
    universityPrefix: 'University',
    showingNotifications(count) {
      return `Showing ${count} notifications`
    },
    stats: {
      unread: {
        label: 'Unread',
        hint: 'Needs your attention first'
      },
      read: {
        label: 'Read',
        hint: 'Already opened and tracked'
      },
      approval: {
        label: 'Approvals',
        hint: 'Waiting for admin approval'
      }
    },
    deadlines: {
      dueToday: 'Due today',
      dayLeft: '1 day left',
      daysLeft(days) {
        return `${days} days left`
      },
      dayOverdue: '1 day overdue',
      daysOverdue(days) {
        return `${days} days overdue`
      }
    }
  },
  th: {
    heroTitle: 'การแจ้งเตือน',
    heroSubtitle: 'เปิดรายการ ดำเนินงานต่อ และขับเคลื่อนงานให้เดินหน้า',
    searchPlaceholder: 'ค้นหาด้วยชื่อข้อตกลง',
    startDateLabel: 'วันที่เริ่มต้น',
    endDateLabel: 'วันที่สิ้นสุด',
    emptyState: 'ไม่พบการแจ้งเตือน',
    dismissNotification: 'ปิดการแจ้งเตือน',
    referencePrefix: 'อ้างอิง',
    universityPrefix: 'มหาวิทยาลัย',
    showingNotifications(count) {
      return `แสดงการแจ้งเตือน ${count} รายการ`
    },
    stats: {
      unread: {
        label: 'ยังไม่ได้อ่าน',
        hint: 'ต้องการความสนใจจากคุณก่อน'
      },
      read: {
        label: 'อ่านแล้ว',
        hint: 'เปิดอ่านและติดตามแล้ว'
      },
      approval: {
        label: 'การอนุมัติ',
        hint: 'กำลังรอการอนุมัติจากผู้ดูแลระบบ'
      }
    },
    deadlines: {
      dueToday: 'ครบกำหนดวันนี้',
      dayLeft: 'เหลือ 1 วัน',
      daysLeft(days) {
        return `เหลือ ${days} วัน`
      },
      dayOverdue: 'เกินกำหนด 1 วัน',
      daysOverdue(days) {
        return `เกินกำหนด ${days} วัน`
      }
    }
  }
}

const NOTIFICATION_ROLES = ['admin', 'committee', 'user']
const NOTIFICATION_POLL_INTERVAL_MS = 30000
const NOTIFICATION_CACHE_MAX_AGE_MS = 30000

export default {
  name: 'NotificationsPage',
  components: {
    AppSectionHero
  },
  data() {
    return {
      notifications: [],
      currentTab: 'all',
      searchQuery: '',
      startDate: '',
      endDate: '',
      lastLoadAt: 0,
      loadPromise: null,
      workspaceRoleVersion: 0,
      pollTimer: null
    }
  },
  computed: {
    localeCode() {
      return String(this.$i18n && this.$i18n.locale ? this.$i18n.locale : 'en').toLowerCase().startsWith('th') ? 'th' : 'en'
    },
    pageText() {
      return PAGE_TEXT[this.localeCode] || PAGE_TEXT.en
    },
    currentRoles() {
      this.workspaceRoleVersion
      const assignedRoles = getAssignedRoles(this.$store).filter(role => NOTIFICATION_ROLES.includes(role))
      const fallbackRoles = assignedRoles.length ? assignedRoles : ['user']
      const workspaceQuery = String(this.$route && this.$route.query && this.$route.query.workspace || '').trim().toLowerCase()
      if (workspaceQuery === 'all' || workspaceQuery === 'multiuser') {
        return fallbackRoles
      }
      if (workspaceQuery) {
        const routeRole = detectRouteRole(this.$store, this.$route)
        if (routeRole && fallbackRoles.includes(routeRole)) return [routeRole]
      }

      const activeRole = getActiveWorkspaceRole(this.$store)
      if (activeRole && fallbackRoles.includes(activeRole)) return [activeRole]

      const workspaceRole = getWorkspaceRole(this.$store)
      if (workspaceRole === 'multiuser') {
        return fallbackRoles
      }

      const routeRole = detectRouteRole(this.$store, this.$route)
      const selectedRole = [routeRole, workspaceRole].find(role => fallbackRoles.includes(role))
      return [selectedRole || fallbackRoles[0]]
    },
    heroStats() {
      return [
        {
          tab: 'unread',
          label: this.pageText.stats.unread.label,
          value: this.unreadNotifications.length,
          hint: this.pageText.stats.unread.hint,
          icon: 'cil-envelope-closed',
          iconClass: 'app-section-stat__icon--danger',
          clickable: true,
          active: this.currentTab === 'unread'
        },
        {
          tab: 'read',
          label: this.pageText.stats.read.label,
          value: this.readNotifications.length,
          hint: this.pageText.stats.read.hint,
          icon: 'cil-envelope-open',
          iconClass: 'app-section-stat__icon--total',
          clickable: true,
          active: this.currentTab === 'read'
        },
        {
          tab: 'approval',
          label: this.pageText.stats.approval.label,
          value: this.notifications.filter(item => item.type === 'approval').length,
          hint: this.pageText.stats.approval.hint,
          icon: 'cil-check-circle',
          iconClass: 'app-section-stat__icon--attention',
          clickable: true,
          active: this.currentTab === 'approval'
        }
      ]
    },
    notificationsCountLabel() {
      return this.pageText.showingNotifications(this.filteredNotifications.length)
    },
    unreadNotifications() {
      return this.notifications.filter(item => this.isUnread(item))
    },
    readNotifications() {
      return this.notifications.filter(item => !this.isUnread(item))
    },
    filteredNotifications() {
      let rows = [...this.notifications]

      if (this.currentTab === 'unread') {
        rows = rows.filter(item => this.isUnread(item))
      } else if (this.currentTab === 'read') {
        rows = rows.filter(item => !this.isUnread(item))
      } else if (this.currentTab !== 'all') {
        rows = rows.filter(item => item.type === this.currentTab)
      }

      if (this.searchQuery) {
        const query = String(this.searchQuery || '').trim().toLowerCase()
        rows = rows.filter(item => String(item.agreementName || '').toLowerCase().includes(query))
      }

      if (this.startDate) {
        const start = new Date(`${this.startDate}T00:00:00`)
        rows = rows.filter(item => this.notificationTimeValue(item) >= start.getTime())
      }

      if (this.endDate) {
        const end = new Date(`${this.endDate}T23:59:59`)
        rows = rows.filter(item => this.notificationTimeValue(item) <= end.getTime())
      }

      return rows.sort((left, right) => this.notificationTimeValue(right) - this.notificationTimeValue(left))
    }
  },
  watch: {
    localeCode() {
      this.loadNotifications()
    },
    '$route.fullPath'() {
      this.handleWorkspaceRoleChange()
    }
  },
  mounted() {
    this.loadNotifications({ forceRefresh: true })
    this.pollTimer = window.setInterval(() => {
      if (canAutoRefresh()) this.loadNotifications({ forceRefresh: true })
    }, NOTIFICATION_POLL_INTERVAL_MS)
    window.addEventListener('focus', this.loadNotifications)
    window.addEventListener('storage', this.loadNotifications)
    window.addEventListener(workflowNotificationEventName(), this.handleNotificationChange)
    window.addEventListener(workspaceRoleEventName(), this.handleWorkspaceRoleChange)
  },
  beforeDestroy() {
    if (this.pollTimer) {
      window.clearInterval(this.pollTimer)
      this.pollTimer = null
    }
    window.removeEventListener('focus', this.loadNotifications)
    window.removeEventListener('storage', this.loadNotifications)
    window.removeEventListener(workflowNotificationEventName(), this.handleNotificationChange)
    window.removeEventListener(workspaceRoleEventName(), this.handleWorkspaceRoleChange)
  },
  methods: {
    handleWorkspaceRoleChange() {
      this.workspaceRoleVersion += 1
      this.lastLoadAt = 0
      this.loadNotifications({ forceRefresh: true })
    },
    handleNotificationChange() {
      this.lastLoadAt = 0
      this.loadNotifications({ forceRefresh: true })
    },
    notificationTimeValue(item) {
      const rawValue = item && item.timestamp ? item.timestamp : ''
      const parsed = new Date(rawValue).getTime()
      if (!Number.isNaN(parsed)) return parsed
      const normalized = String(rawValue || '').trim().replace(' ', 'T')
      const normalizedParsed = new Date(normalized).getTime()
      return Number.isNaN(normalizedParsed) ? 0 : normalizedParsed
    },
    async loadNotifications(options = {}) {
      const currentRoles = this.currentRoles
      const storedItems = listWorkflowNotificationsForRoles(currentRoles, { includeDismissed: true })

      const cacheMaxAge = typeof options.cacheMaxAge === 'number' ? options.cacheMaxAge : NOTIFICATION_CACHE_MAX_AGE_MS
      if (this.loadPromise) return this.loadPromise
      if (!options.forceRefresh && this.lastLoadAt && (Date.now() - this.lastLoadAt) < cacheMaxAge) return

      this.loadPromise = (async () => {
        const queueItems = await buildWorkflowNotificationsForRoles(currentRoles, {
          cacheMaxAge,
          forceRefresh: !!options.forceRefresh
        })
        const mergedQueueItems = mergeStoredWorkflowNotificationState(queueItems, storedItems)
        const deduped = dedupeWorkflowNotifications(mergedQueueItems)
        this.notifications = deduped
          .map(item => ({
            ...item,
            agreementName: item && item.agreementName
              ? item.agreementName
              : (item && item.title ? item.title : '')
          }))
        this.lastLoadAt = Date.now()
      })()

      try {
        await this.loadPromise
      } finally {
        this.loadPromise = null
      }
    },
    formatDateTime(value) {
      return formatDateTimeWithPreferences(value)
    },
    startOfDay(value) {
      const date = new Date(value)
      return new Date(date.getFullYear(), date.getMonth(), date.getDate())
    },
    resolveDeadlineDays(item) {
      const createdAt = new Date(item && item.timestamp ? item.timestamp : new Date().toISOString())
      const fallbackDays = 7
      const dueAt = item && item.dueAt
        ? new Date(item.dueAt)
        : new Date(createdAt.getFullYear(), createdAt.getMonth(), createdAt.getDate() + fallbackDays)
      const today = this.startOfDay(new Date())
      const dueDay = this.startOfDay(dueAt)
      return Math.round((dueDay.getTime() - today.getTime()) / (24 * 60 * 60 * 1000))
    },
    deadlineLabel(item) {
      const daysLeft = this.resolveDeadlineDays(item)
      if (daysLeft < 0) {
        const overdueDays = Math.abs(daysLeft)
        return overdueDays === 1
          ? this.pageText.deadlines.dayOverdue
          : this.pageText.deadlines.daysOverdue(overdueDays)
      }
      if (daysLeft === 0) return this.pageText.deadlines.dueToday
      if (daysLeft === 1) return this.pageText.deadlines.dayLeft
      return this.pageText.deadlines.daysLeft(daysLeft)
    },
    deadlineState(item) {
      const daysLeft = this.resolveDeadlineDays(item)
      if (daysLeft < 0) return 'overdue'
      if (daysLeft <= 1) return 'urgent'
      if (daysLeft <= 3) return 'soon'
      return 'normal'
    },
    referenceLabel(reference) {
      return `${this.pageText.referencePrefix} ${reference}`
    },
    notificationContextLabel(item) {
      const partnerName = String(
        item && (
          item.partnerName ||
          item.partner ||
          item.universityName
        ) || ''
      ).trim()
      if (partnerName) {
        return `${this.pageText.universityPrefix}: ${partnerName}`
      }
      return item && item.reference ? this.referenceLabel(item.reference) : ''
    },
    isUnread(item) {
      return !(item && item.read)
    },
    selectStatusCard(stat) {
      const nextTab = stat && stat.tab ? stat.tab : 'all'
      this.currentTab = this.currentTab === nextTab ? 'all' : nextTab
    },
    openNotification(item) {
      if (!item || !item.targetPath) return
      markWorkflowNotificationRead(item.id, item)
      this.notifications = this.notifications.map((entry) => (
        workflowNotificationMatches(entry, item) ? { ...entry, read: true } : entry
      ))
      this.$router.push(item.targetPath)
    },
    dismissNotification(item) {
      if (!item) return
      dismissWorkflowNotification(item.id, item)
      this.notifications = this.notifications.filter(entry => !workflowNotificationMatches(entry, item))
    }
  }
}
</script>

<style scoped lang="scss">
@import "../shared/workspace-consistency.scss";

@include workspace-toolbar-search;

.notifications-page {
  min-height: 100vh;
  padding: 24px;
}

.notifications-shell,
.notification-card,
.empty-state {
  background: #fff;
  border: 1px solid #eaded4;
  border-radius: 22px;
  box-shadow: 0 14px 34px rgba(49, 31, 20, 0.06);
}

.notifications-shell {
  overflow: hidden;
}

.notifications-head {
  align-items: center;
  border-bottom: 1px solid #efe4da;
  display: flex;
  gap: 12px;
  justify-content: space-between;
  padding: 18px;
}

.notifications-head__copy {
  color: #7c6e78;
  font-weight: 700;
}

.notifications-list {
  display: grid;
  gap: 14px;
  padding: 18px;
}

.notification-card {
  border: 0;
  cursor: pointer;
  padding: 20px;
  text-align: left;
  transition: transform 0.18s ease, box-shadow 0.18s ease, background 0.18s ease;
  width: 100%;
}

.notification-card:hover,
.notification-card:focus {
  box-shadow: 0 18px 36px rgba(49, 31, 20, 0.1);
  outline: none;
  transform: translateY(-1px);
}

.notification-card--unread {
  background: linear-gradient(180deg, #fff8f8 0%, #fff1f2 100%);
  border: 1px solid #f3b1b7;
  box-shadow: 0 16px 34px rgba(185, 28, 28, 0.12);
  padding-left: 36px;
  position: relative;
}

.notification-card--unread::before {
  background: linear-gradient(180deg, #dc2626 0%, #991b1b 100%);
  border-radius: 999px;
  content: '';
  height: calc(100% - 28px);
  left: 16px;
  position: absolute;
  top: 14px;
  width: 4px;
}

.notification-top,
.notification-meta,
.notification-action {
  align-items: center;
  display: flex;
  gap: 12px;
  justify-content: space-between;
}

.notification-badges {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: flex-end;
}

.notification-source {
  color: #8c1515;
  font-size: 0.76rem;
  font-weight: 800;
  letter-spacing: 0.1em;
  margin-bottom: 6px;
  text-transform: uppercase;
}

.notification-card h2 {
  color: #472027;
  font-size: 1.12rem;
  font-weight: 800;
  margin: 0;
}

.notification-deadline {
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 800;
  padding: 6px 10px;
  text-transform: uppercase;
}

.notification-dismiss {
  align-items: center;
  background: #fff;
  border: 1px solid #eadacc;
  border-radius: 999px;
  color: #7e1517;
  cursor: pointer;
  display: inline-flex;
  height: 34px;
  justify-content: center;
  padding: 0;
  transition: background 0.18s ease, border-color 0.18s ease, transform 0.18s ease;
  width: 34px;
}

.notification-dismiss:hover,
.notification-dismiss:focus {
  background: #fff5f5;
  border-color: #dba2a2;
  outline: none;
  transform: translateY(-1px);
}

.notification-deadline--urgent {
  background: linear-gradient(135deg, #b91c1c 0%, #991b1b 100%);
  color: #fff7f7;
  box-shadow: 0 8px 18px rgba(185, 28, 28, 0.22);
}

.notification-deadline--overdue {
  background: linear-gradient(135deg, #7f1d1d 0%, #5f1717 100%);
  color: #fff7f7;
  box-shadow: 0 8px 18px rgba(127, 29, 29, 0.24);
}

.notification-deadline--soon {
  background: linear-gradient(135deg, #b91c1c 0%, #991b1b 100%);
  color: #fff7f7;
  box-shadow: 0 8px 18px rgba(185, 28, 28, 0.22);
}

.notification-deadline--normal {
  background: linear-gradient(135deg, #b91c1c 0%, #991b1b 100%);
  color: #fff7f7;
  box-shadow: 0 8px 18px rgba(185, 28, 28, 0.22);
}

.notification-message {
  color: #706771;
  line-height: 1.65;
  margin: 14px 0;
}

.notification-meta {
  color: #8a7b84;
  font-size: 0.86rem;
  margin-bottom: 14px;
}

.notification-next {
  color: #5d4b53;
  flex: 1;
  line-height: 1.5;
}

.empty-state {
  color: #7c6e78;
  padding: 24px;
  text-align: center;
}

.toolbar {
  justify-content: space-between;
}

.search-box {
  flex: 1 1 420px;
  width: auto;
}

.toolbar-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  flex: 0 0 auto;
}

.date-input {
  min-width: 160px;
}

@media (max-width: 768px) {
  .notifications-page {
    padding: 16px;
  }

  .notifications-head {
    align-items: stretch;
    flex-direction: column;
  }

  .notification-top,
  .notification-meta,
  .notification-action {
    align-items: flex-start;
    flex-direction: column;
  }

  .hero-count {
    min-width: 100%;
    width: 100%;
  }
}
</style>
